/**
 * Phone Input with DDI (international dialing code) selector
 * Feature: shared
 *
 * Controlled component compatible with react-hook-form.
 * Shows a DDI dropdown on the left and a phone input on the right,
 * styled as a unified input group matching shadcn/ui aesthetics.
 *
 * When DDI is "55" (Brazil), applies Brazilian phone mask.
 * For other countries, accepts free-form digits only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { applyPhoneMask } from '@/features/shared/utils/phoneUtils'
import {
  COUNTRIES,
  findCountryByDialCode,
  BRAZIL,
  type Country,
} from '@/features/shared/data/countries'

// ============================================================================
// Types
// ============================================================================

interface PhoneInputWithDDIProps {
  value: string
  countryCode: string
  onPhoneChange: (phone: string) => void
  onCountryCodeChange: (code: string) => void
  error?: string
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function PhoneInputWithDDI({
  value,
  countryCode,
  onPhoneChange,
  onCountryCodeChange,
  error,
  disabled = false,
}: PhoneInputWithDDIProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Resolve current country from dial code
  const selectedCountry = useMemo(
    () => findCountryByDialCode(countryCode) ?? BRAZIL,
    [countryCode]
  )

  // Filter countries by search term (name or dial code)
  const filteredCountries = useMemo(() => {
    if (!search.trim()) return COUNTRIES
    const term = search.toLowerCase().trim()
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.dialCode.includes(term) ||
        c.code.toLowerCase().includes(term)
    )
  }, [search])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearch('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setSearch('')
        triggerRef.current?.focus()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleToggle = useCallback(() => {
    if (disabled) return
    setIsOpen((prev) => !prev)
    setSearch('')
  }, [disabled])

  const handleSelectCountry = useCallback(
    (country: Country) => {
      onCountryCodeChange(country.dialCode)
      setIsOpen(false)
      setSearch('')
      // Clear phone value when changing country to avoid invalid formats
      onPhoneChange('')
    },
    [onCountryCodeChange, onPhoneChange]
  )

  const handlePhoneInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value

      if (countryCode === '55') {
        // Apply Brazilian phone mask
        const { formatted } = applyPhoneMask(inputValue)
        onPhoneChange(formatted)
      } else {
        // Free-form digits only for other countries
        const digits = inputValue.replace(/\D/g, '')
        onPhoneChange(digits)
      }
    },
    [countryCode, onPhoneChange]
  )

  const isBrazil = countryCode === '55'

  return (
    <div className="relative">
      <div
        className={cn(
          'flex items-center rounded-md border bg-background transition-colors',
          error
            ? 'border-destructive focus-within:ring-destructive'
            : 'border-input focus-within:ring-ring',
          'focus-within:ring-2 focus-within:ring-offset-2 ring-offset-background',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {/* DDI Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={cn(
            'flex items-center gap-1.5 px-3 h-10 border-r border-input',
            'text-sm font-medium text-foreground',
            'hover:bg-accent/50 transition-colors',
            'rounded-l-md shrink-0',
            'focus:outline-none',
            disabled && 'pointer-events-none'
          )}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Selecionar codigo do pais"
        >
          <span className="text-base leading-none">{selectedCountry.flag}</span>
          <span className="text-muted-foreground">+{selectedCountry.dialCode}</span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Phone Input */}
        <input
          type="tel"
          value={value}
          onChange={handlePhoneInput}
          disabled={disabled}
          placeholder={isBrazil ? '(XX) XXXXX-XXXX' : 'Numero de telefone'}
          className={cn(
            'flex-1 h-10 px-3 py-2 text-base bg-transparent',
            'placeholder:text-muted-foreground',
            'focus:outline-none',
            'disabled:cursor-not-allowed',
            'md:text-sm'
          )}
        />
      </div>

      {/* Error message */}
      {error && <p className="text-sm text-destructive mt-1.5">{error}</p>}

      {/* Country Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          role="listbox"
          className={cn(
            'absolute z-[9999] mt-1 w-full min-w-[280px]',
            'rounded-md border border-input bg-popover text-popover-foreground shadow-md',
            'animate-in fade-in-0 zoom-in-95'
          )}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-input">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar pais ou codigo..."
                className={cn(
                  'w-full h-9 pl-8 pr-3 rounded-md text-sm',
                  'bg-background border border-input',
                  'placeholder:text-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ring-offset-background'
                )}
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-[200px] overflow-y-auto py-1">
            {filteredCountries.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nenhum pais encontrado
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={`${country.code}-${country.dialCode}`}
                  type="button"
                  role="option"
                  aria-selected={country.dialCode === countryCode}
                  onClick={() => handleSelectCountry(country)}
                  className={cn(
                    'flex items-center gap-3 w-full px-3 py-2 text-sm',
                    'hover:bg-accent hover:text-accent-foreground transition-colors',
                    'focus:outline-none focus:bg-accent focus:text-accent-foreground',
                    country.dialCode === countryCode &&
                      country.code === selectedCountry.code &&
                      'bg-accent/50 font-medium'
                  )}
                >
                  <span className="text-base leading-none shrink-0">{country.flag}</span>
                  <span className="flex-1 text-left truncate">{country.name}</span>
                  <span className="text-muted-foreground shrink-0">+{country.dialCode}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
